'use client';

import React, { useEffect } from 'react';
import { SUBJECTS } from '../../../../../apps/frontend/src/utils/strings';
import { useSelector } from 'react-redux';
import { Check, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { TabsContainer, Tab } from '../../utils/SubjectTabs';

const SubjectTabs = ({
  activeSubject,
  onSubjectChange,
  subjects = [],
  showOverallTab = false,
  color, // active tab highlight color
}) => {
  const location = useLocation();
  const { isDark } = useSelector((state) => state.user);

  const headers = showOverallTab ? [SUBJECTS.OVERALL, ...subjects] : subjects;

  const normalizedHeaders = headers.map((header) => (typeof header === 'string' ? { title: header } : header));

  // 🔥 Ensure something valid is always selected
  useEffect(() => {
    const isActivePresent = normalizedHeaders.some(
      (item) => item._id?.toString() === activeSubject?.toString() || item.title?.toLowerCase?.() === activeSubject,
    );

    if (!isActivePresent && normalizedHeaders.length > 0) {
      // Prefer Overall if available
      const fallback =
        normalizedHeaders.find(
          (item) =>
            item.title &&
            item.title.toLowerCase?.() ===
              (typeof SUBJECTS.OVERALL === 'string'
                ? SUBJECTS.OVERALL.toLowerCase()
                : SUBJECTS.OVERALL.title?.toLowerCase()),
        ) || normalizedHeaders[0];

      onSubjectChange(fallback._id || fallback.title?.toLowerCase?.());
    }
  }, [normalizedHeaders, activeSubject, onSubjectChange]);

  const handleTabClick = (tab) => {
    if (!tab) return;
    onSubjectChange(tab._id || tab.title?.toLowerCase?.());
  };

  const activeTabColor =
    normalizedHeaders.find((item) => item._id?.toString() === activeSubject?.toString())?.color || color;

  return (
    <div className="w-full overflow-x-auto">
      <TabsContainer color={activeTabColor} className="flex w-max sm:w-full">
        {normalizedHeaders.map((header, index) => {
          const isActive = header._id === activeSubject || header.title?.toLowerCase?.() === activeSubject;

          return (
            <Tab
              key={index}
              isActive={isActive}
              color={isActive ? color : 'var(--q3-neutral-light)'}
              tabColor={header.color}
              bgColor="var(--q3-surface-dim)"
              className="flex-1 sm:flex-initial min-w-fit"
              onClick={() => handleTabClick(header)}
            >
              <div className="flex items-center justify-center gap-1 whitespace-nowrap w-full">
                {header.title === 'Correct' ? (
                  <Check size={16} className="text-green-500" />
                ) : header.title === 'Incorrect' ? (
                  <X size={16} className="text-red-500" />
                ) : header.icon ? (
                  <img
                    src={isDark ? header.darkIcon || header.icon : header.icon}
                    alt={header.title}
                    className="w-4 h-4"
                  />
                ) : null}
                <span className="text-sm">{header.title}</span>
              </div>
            </Tab>
          );
        })}
      </TabsContainer>
    </div>
  );
};

export default SubjectTabs;
